import type { ReactNode } from "react";
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

/**
 * The narrowest an action table is laid out. Below it the table scrolls
 * sideways rather than squeezing its columns, which would push the actions out
 * of view; a caller with room to rearrange can switch layout at this width
 * instead.
 */
export const ACTION_TABLE_MIN_WIDTH = 720;

/**
 * A table with each row's actions in a final right-aligned column.
 *
 * The actions column has no visible heading because its buttons label
 * themselves; it is named for assistive technology so the column is not read
 * out as blank.
 */
export const ActionTable = ({
  columns,
  actionsLabel,
  children,
}: {
  columns: readonly string[];
  actionsLabel: string;
  children: ReactNode;
}) => (
  <TableContainer>
    <Table size="small" sx={{ minWidth: ACTION_TABLE_MIN_WIDTH }}>
      <TableHead>
        <TableRow>
          {columns.map((column) => (
            <TableCell key={column}>{column}</TableCell>
          ))}
          <TableCell align="right" aria-label={actionsLabel} />
        </TableRow>
      </TableHead>
      <TableBody>{children}</TableBody>
    </Table>
  </TableContainer>
);

/** One titled `ActionTable`. */
export const ActionTableSection = ({
  title,
  columns,
  actionsLabel,
  children,
}: {
  title: string;
  columns: readonly string[];
  actionsLabel: string;
  children: ReactNode;
}) => (
  <Stack spacing={1} component="section" aria-label={title}>
    <Typography variant="subtitle1" component="h3">
      {title}
    </Typography>
    <ActionTable columns={columns} actionsLabel={actionsLabel}>
      {children}
    </ActionTable>
  </Stack>
);
